import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const stats = [
  { number: '10,000+', label: 'Trees Planted', icon: '🌳' },
  { number: '50+', label: 'Cities Covered', icon: '🏙️' },
  { number: '5,000+', label: 'Volunteers', icon: '🤝' },
  { number: '12+', label: 'Forest Dept. Partners', icon: '🏛️' },
];

const features = [
  {
    icon: '🌱',
    title: 'Plant a Love Tree',
    desc: 'Gift a tree in honor of someone special. Every tree carries a memory — green, living, eternal.',
  },
  {
    icon: '🧹',
    title: 'Clean India Drive',
    desc: 'Community-led cleanliness campaigns transforming neighborhoods and public spaces across India.',
  },
  {
    icon: '🌾',
    title: 'Agri Startup',
    desc: 'Empowering farmers with sustainable agri-products, connecting nature-first agriculture to markets.',
  },
  {
    icon: '🌐',
    title: 'Global Reach',
    desc: 'Coordinating with Forest Departments to scale environmental impact from local to global.',
  },
];

function useScrollFade() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('visible'); },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function FadeSection({ children, className = '' }) {
  const ref = useScrollFade();
  return <div ref={ref} className={`fade-in ${className}`}>{children}</div>;
}

export default function Home() {
  return (
    <div className="home">
      {/* HERO */}
      <section className="hero">
        <div className="hero__bg">
          <div className="hero__particles">
            {[...Array(20)].map((_, i) => (
              <div key={i} className="hero__particle" style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 4}s`,
              }} />
            ))}
          </div>
        </div>
        <div className="hero__overlay" />
        <div className="hero__content container">
          <div className="hero__badge">
            <span>🌍</span> Y2K Global Nexus — calltree.in
          </div>
          <h1 className="hero__headline">
            Plant a Tree.<br />
            <span className="hero__headline-gold">Share a Memory.</span><br />
            Heal the Planet.
          </h1>
          <p className="hero__sub">
            Life is full of memories. Memories are made with beautiful moments.<br />
            Give the most meaningful gift — a living, breathing tree — and live forever in their memories.
          </p>
          <div className="hero__actions">
            <Link to="/contact" className="btn-gold hero__cta-main">
              🌱 Plant a Love Tree
            </Link>
            <Link to="/calltree" className="btn-outline hero__cta-secondary">
              Discover CallTree
            </Link>
          </div>
          <div className="hero__scroll-hint">
            <div className="hero__scroll-dot" />
            <span>Scroll to explore</span>
          </div>
        </div>
      </section>

      {/* POEM SECTION */}
      <section className="poem-section">
        <div className="container">
          <FadeSection>
            <div className="poem-card">
              <div className="poem-card__header">
                <h2 className="poem-card__title">Plant a Love</h2>
                <p className="poem-card__org">A Y2K Global Nexus × CallTree Initiative</p>
              </div>
              <div className="poem-card__lines">
                {[
                  'This Life is full of Memories',
                  'Memories are made with beautiful Moments',
                  'Moments can be created',
                  'Present him/her your beautiful Memories',
                  'Is there any best present you could give',
                  'her / him better than your memory',
                  'Memories are ever Green',
                  'Greener trees holds Green Memories',
                  'When you present a Green tree — it holds your green Memories',
                  'You Live-in her / his Memories',
                  'This Nation knowing you Love',
                  'The air is Purified',
                  'Love and air are the purest of its nature',
                  'Come on — Lets get cleansed',
                  'On this Valentine\'s day',
                  'Register your participation and share your Love',
                ].map((line, i) => (
                  <p key={i} className="poem-card__line" style={{ animationDelay: `${i * 0.1}s` }}>
                    {line}
                  </p>
                ))}
              </div>
              <div className="poem-card__cta">
                <Link to="/contact" className="btn-primary">
                  Register Your Participation
                </Link>
              </div>
            </div>
          </FadeSection>
        </div>
      </section>

      {/* STATS */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map(({ number, label, icon }, i) => (
              <FadeSection key={i}>
                <div className="stat-card" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className="stat-card__icon">{icon}</div>
                  <div className="stat-card__number">{number}</div>
                  <div className="stat-card__label">{label}</div>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="features-section section-padding">
        <div className="container">
          <FadeSection>
            <div className="section-header">
              <span className="tag">What We Do</span>
              <h2 className="section-title">Environment. Community. Growth.</h2>
              <p className="section-subtitle">
                Y2K Global Nexus drives environmental protection through tree planting, awareness campaigns,
                clean drives, and sustainable agri-products — all in coordination with Forest Departments.
              </p>
            </div>
          </FadeSection>
          <div className="features-grid">
            {features.map(({ icon, title, desc }, i) => (
              <FadeSection key={i}>
                <div className="feature-card" style={{ transitionDelay: `${i * 0.1}s` }}>
                  <div className="feature-card__icon">{icon}</div>
                  <h3 className="feature-card__title">{title}</h3>
                  <p className="feature-card__desc">{desc}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION BANNER */}
      <section className="mission-banner">
        <div className="mission-banner__bg" />
        <div className="container mission-banner__inner">
          <FadeSection>
            <span className="tag">Our Mission</span>
            <h2 className="mission-banner__title">
              From Local Roots<br />to <span className="gold-text">Global Impact</span>
            </h2>
            <p className="mission-banner__desc">
              Y2K Global Nexus coordinates with the Forest Department to plant trees, purify air,
              and create a sustainable future. These activities are being escalated to the global level — Y2K will do that.
            </p>
            <div className="mission-banner__actions">
              <Link to="/about" className="btn-primary">Learn About Y2K</Link>
              <Link to="/activities" className="btn-outline" style={{ borderColor: 'white', color: 'white' }}>
                See Our Activities
              </Link>
            </div>
          </FadeSection>
        </div>
      </section>

      {/* CALLTREE HIGHLIGHT */}
      <section className="calltree-highlight section-padding">
        <div className="container">
          <div className="calltree-highlight__grid">
            <FadeSection>
              <div className="calltree-highlight__content">
                <span className="tag">Featured Project</span>
                <h2 className="section-title">CallTree Initiative</h2>
                <p className="section-subtitle">
                  calltree.in is the digital home for all our environmental activities conducted in
                  coordination with the Forest Department. Register, plant, and track your tree —
                  building memories that last generations.
                </p>
                <ul className="calltree-highlight__points">
                  <li>🌳 Valentine's Day Tree Gifting Campaign</li>
                  <li>📋 Forest Department Collaboration</li>
                  <li>🌍 Scaling to Global Level</li>
                  <li>📱 Track Your Tree Online</li>
                </ul>
                <Link to="/calltree" className="btn-primary" style={{ marginTop: '24px', display: 'inline-flex' }}>
                  Explore CallTree →
                </Link>
              </div>
            </FadeSection>
            <FadeSection>
              <div className="calltree-highlight__visual">
                <div className="calltree-visual-card">
                  <div className="calltree-visual-card__emoji">🌲</div>
                  <div className="calltree-visual-card__text">
                    <strong>Your Tree</strong>
                    <span>Planted in your name</span>
                    <span>GPS tracked, forever yours</span>
                  </div>
                </div>
                <div className="calltree-visual-card calltree-visual-card--alt">
                  <div className="calltree-visual-card__emoji">💚</div>
                  <div className="calltree-visual-card__text">
                    <strong>Plant a Love</strong>
                    <span>Gift it to someone special</span>
                    <span>A memory ever green</span>
                  </div>
                </div>
                <div className="calltree-visual-card">
                  <div className="calltree-visual-card__emoji">🌍</div>
                  <div className="calltree-visual-card__text">
                    <strong>Global Network</strong>
                    <span>Forest Dept. certified</span>
                    <span>Expanding worldwide</span>
                  </div>
                </div>
              </div>
            </FadeSection>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="cta-banner">
        <div className="container">
          <FadeSection>
            <h2 className="cta-banner__title">
              Ready to <span className="gold-text">Plant Your Memory?</span>
            </h2>
            <p className="cta-banner__sub">
              Join thousands of people creating living memories for their loved ones.
              Register today and be part of the green revolution.
            </p>
            <Link to="/contact" className="btn-gold">
              🌱 Register Now — It's Free
            </Link>
          </FadeSection>
        </div>
      </section>
    </div>
  );
}
